import { useMemo, useRef, useState } from "react";
import { Link, Outlet } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { SuiteAppBar } from "@athavita/ui-theme";
import { useAppearanceSync } from "@athavita/ui-theme";
import { ShellHeaderContext } from "./ShellHeader";

export function DocsShell() {
    const auth = useAuth();
    const profile = auth.user?.profile;
    const email = profile?.email;
    const user = { name: profile?.name ?? email ?? "Signed in", email };

    // The suite look follows the account (Drive BFF /api/me/appearance). Docs'
    // token carries the Drive audience (athavita.drive scope).
    useAppearanceSync(import.meta.env.VITE_DRIVE_BFF_BASE_URL, auth.user?.access_token);

    // The editor portals its file name + save status into `headerSlot`, and
    // registers a guard so the brand link can't silently drop unsaved edits.
    // The slot is flex-1 so the editor can put its Share button at the right.
    const [headerSlot, setHeaderSlot] = useState<HTMLDivElement | null>(null);
    const leaveGuardRef = useRef<(() => boolean) | null>(null);
    const headerContext = useMemo(() => ({
        slot: headerSlot,
        setLeaveGuard: (guard: (() => boolean) | null) => { leaveGuardRef.current = guard; }
    }), [headerSlot]);

    return (
        <ShellHeaderContext.Provider value={headerContext}>
            <div className="flex h-screen flex-col bg-background text-foreground">
                <a
                    href="#main-content"
                    className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded-md focus:bg-primary focus:px-3 focus:py-1 focus:text-sm focus:text-primary-foreground"
                >
                    Skip to main content
                </a>
                <SuiteAppBar
                    app="docs"
                    user={user}
                    onSignOut={() => void auth.signoutRedirect()}
                    renderBrand={(brand) => (
                        <Link
                            to="/"
                            title="Docs home"
                            onClick={(e) => {
                                if (leaveGuardRef.current && !leaveGuardRef.current()) e.preventDefault();
                            }}
                            className="suite-appbar-btn flex items-center rounded px-1.5 py-1"
                        >
                            {brand}
                        </Link>
                    )}
                    title={<div ref={setHeaderSlot} className="flex min-w-0 flex-1 items-center gap-1.5" />}
                />
                <main id="main-content" tabIndex={-1} className="flex min-h-0 flex-1 flex-col">
                    <Outlet />
                </main>
            </div>
        </ShellHeaderContext.Provider>
    );
}
